import { useEffect, useState } from "react";
import { BASE } from "./api";

export interface Branding {
    orgName: string;
    tagline: string | null;
    hasLogo: boolean;
    logoUrl: string | null;
    supportPhone: string | null;
    supportEmail: string | null;
    supportHours: string | null;
    safetyContact: string | null;
}

const DEFAULT: Branding = {
    orgName: "TrustCare Support",
    tagline: null,
    hasLogo: false,
    logoUrl: null,
    supportPhone: null,
    supportEmail: null,
    supportHours: null,
    safetyContact: null,
};

/** Module-level cache: branding changes rarely and every page wants it. */
let cached: Branding | null = null;

/**
 * The organisation identity the instance runs under. Public endpoint — the
 * sign-in page renders it before anyone is authenticated.
 *
 * Note what is NOT here: any flag for the "Powered by TrustCare" mark. The
 * components render that unconditionally; a white-labelled instance shows the
 * buyer's identity up top and TrustCare's provenance underneath, always.
 */
export const useBranding = (): Branding => {
    const [branding, setBranding] = useState<Branding>(cached ?? DEFAULT);

    useEffect(() => {
        if (cached) return;
        let live = true;
        // A relative "/api/branding" only ever worked locally, where Vite's
        // dev proxy strips the prefix and forwards the rest — production has
        // no such proxy in front of the static build, so this has to resolve
        // through the same base URL every other request in the app uses.
        fetch(`${BASE}/branding`)
            .then((r) => (r.ok ? r.json() : DEFAULT))
            .then((data: Branding) => {
                // The server hands back its own mount path (also "/api"-
                // prefixed for historical reasons); rebase it the same way.
                if (data.logoUrl) {
                    data.logoUrl = `${BASE}${data.logoUrl.replace(/^\/api/, "")}`;
                }
                cached = data;
                if (live) setBranding(data);
            })
            .catch(() => undefined);
        return () => { live = false; };
    }, []);

    return branding;
};

/** Call after an admin saves branding so the next render refetches. */
export const invalidateBranding = (): void => {
    cached = null;
};
